import { Injectable } from '@angular/core';

import { CatalogObject, CatalogObjectSelection, CatalogObjectTypes, ResultPage } from '@mirage-projects/cw-models';

import { PaginationData } from '../../models/pagination-data';
import { ObjectBrowserSelectionService } from './selection.service';

/** Service that contains the Object browser's business logic */
@Injectable({ providedIn: 'root' })
export class ObjectBrowserService {
    private readonly FIRST_PAGE = 1;
    private readonly DEFAULT_PAGE_SIZE = 12;

    constructor(private selectionService: ObjectBrowserSelectionService) {}

    /**
     * Selects and unselects cards depending the selection mode (multiple or only one selection)
     * @returns The card id if it has been selected
     */
    updateSelectedCards(
        e: CatalogObjectSelection,
        data: ResultPage<CatalogObjectSelection> | null,
        multipleSelection: boolean,
        lastCardSelectedId: number | undefined,
        type: CatalogObjectTypes
    ): number | undefined {
        let element: CatalogObjectSelection | undefined;
        if (data) {
            element = data.results.find((item) => item.catalogObject.id === e.catalogObject.id);
        }
        this.selectionService.toggleSelection(element, type);

        if (multipleSelection) {
            return;
        }

        if (lastCardSelectedId) {
            this.updateCardSelection(lastCardSelectedId, false, data);

            const oldSelection = this.selectionService.getElementInSelectionById(lastCardSelectedId, type);
            if (oldSelection) {
                this.selectionService.unselect(oldSelection, type);
            }
        }

        return e.selected ? e.catalogObject.id : undefined;
    }

    /**
     * Updates the internal selection data with the new value passed from the card
     */
    updateCardSelection(cardId: number, newValue: boolean, data: ResultPage<CatalogObjectSelection> | null): void {
        const index = data?.results.findIndex((item) => item.catalogObject.id === cardId);

        if (typeof index === 'number' && index >= 0) {
            const card = data?.results[index];

            if (card) {
                card.selected = newValue;
            }
        }
    }

    /**
     * Get the paginated list of selected elements
     * @param page Current page
     * @param pageSize Size of a page
     * @param dataType Defines which type of catalog object has to get the elements
     */
    getSelectedElements(
        page: number = this.FIRST_PAGE,
        pageSize: number = this.DEFAULT_PAGE_SIZE,
        dataType: CatalogObjectTypes
    ): ResultPage<CatalogObjectSelection> {
        const pageXsize = (page - 1) * pageSize;

        const elements = this.selectionService.getSelectedElements(dataType)?.slice(pageXsize, pageXsize + pageSize) as CatalogObject[];
        const catalogSelection = elements.map((item) => {
            return {
                selected: true,
                catalogObject: item,
            };
        });

        const res: ResultPage<CatalogObjectSelection> = {
            count: this.selectionService.getNumberSelectedElements(),
            results: catalogSelection,
        };

        return res;
    }

    /**
     * Checks if the current page number is valid having into account the quantity of elements selected.
     * If the are less elements to enable the page, a new one is calculated
     * @param pagination Pagination data
     */
    getValidPage(pagination: PaginationData): number {
        const selectedElements = this.selectionService.getNumberSelectedElements();
        const maxPage = Math.ceil(selectedElements / pagination.pageSize);

        return Math.min(maxPage, pagination.currentPage);
    }
}
